import { useLicense } from "@coach/license-client";
import { useQuery } from "@tanstack/react-query";
import {
  Brush,
  CalendarDays,
  ClipboardList,
  Compass,
  Image as ImageIcon,
  LayoutGrid,
} from "lucide-react";
import { useNavigate } from "react-router";
import { AppHeader } from "../components/app-header/index.js";
import { CoachIdentity } from "../components/home/coach-identity.js";
import { ModuleCard } from "../components/home/module-card.js";
import { PitchBackdrop } from "../components/home/pitch-backdrop.js";
import { StatusDock } from "../components/home/status-dock.js";
import { isTauri } from "../lib/local-media.js";
import {
  useActiveOrgLogo,
  useActiveOrgName,
  useActiveTeamLeague,
  useActiveTeamName,
  useActiveUserImage,
  useActiveUserName,
} from "../lib/use-active-org.js";
import { useSync } from "../providers/sync-provider";
import { listPlayers } from "../repos/players.js";

import "../components/home/home.css";

/**
 * Open the external browser appropriately for the runtime:
 * - Tauri: false (caller falls back to in-app /browser route with native webview)
 * - Installed PWA (display-mode: standalone): popup window with explicit size,
 *   so it appears as its own window instead of being trapped in the PWA frame.
 * - Regular web tab: plain new tab.
 * Returns true if it handled the open, false if the caller should navigate in-app.
 */
function openExternalIfWeb(url: string): boolean {
  if (isTauri()) return false;
  const isStandalonePwa =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari uses the non-standard navigator.standalone flag.
      (navigator as Navigator & { standalone?: boolean }).standalone === true);
  const features = isStandalonePwa
    ? "noopener,noreferrer,popup=yes,width=1200,height=800"
    : "noopener,noreferrer";
  window.open(url, "_blank", features);
  return true;
}

export function HomePage() {
  const navigate = useNavigate();
  const { db, orgId, defaultTeamId } = useSync();
  const { state: licenseState } = useLicense();
  const licenseValid = licenseState.kind === "valid" || licenseState.kind === "grace";

  const playersQ = useQuery({
    queryKey: ["home", "players", orgId, defaultTeamId],
    enabled: !!db && !!orgId && !!defaultTeamId,
    queryFn: () => listPlayers(db!, orgId!, defaultTeamId!),
  });

  const playerCount = playersQ.data?.length ?? 0;

  const cards: Array<{
    title: string;
    meta: string;
    Icon: typeof LayoutGrid;
    accent: string;
    to: string;
  }> = [
    {
      title: "Taktikboard",
      meta: "Aufstellungen & Drills",
      Icon: LayoutGrid,
      accent: "#97fc00", // pitch-lime
      to: "/tactics",
    },
    {
      title: "Spieler",
      meta: playerCount ? `${playerCount} im Kader` : "Kader anlegen",
      Icon: ClipboardList,
      accent: "#007aff", // tactical-blue
      to: "/players",
    },
    {
      title: "Bilder & Videos",
      meta: "Medienbibliothek",
      Icon: ImageIcon,
      accent: "#a855f7", // violet
      to: "/media",
    },
    {
      title: "Kalender",
      meta: "Termine & Spiele",
      Icon: CalendarDays,
      accent: "#ff3b30", // tactical-red
      to: "/calendar",
    },
    {
      title: "Whiteboard",
      meta: "Skizzen & Notizen",
      Icon: Brush,
      accent: "#38bdf8", // sky — unique vs Taktikboard (pitch-lime)
      to: "/whiteboard",
    },
    {
      title: "Browser",
      meta: "Web ohne App-Wechsel",
      Icon: Compass,
      accent: "#fbbf24", // amber
      to: "/browser",
    },
  ];

  // Server-side identity (Better Auth /me). The home greeting always
  // belongs to the signed-in user. Coach=User in v2, so there is no
  // separate local coach row to fall back to.
  const orgLogo = useActiveOrgLogo();
  const orgNameMe = useActiveOrgName();
  const activeTeamServer = useActiveTeamName();
  const activeTeamLeagueMe = useActiveTeamLeague();
  const userNameMe = useActiveUserName();
  const userImageMe = useActiveUserImage();

  const isAutoTeamName = (name: string | null | undefined): boolean =>
    !name || name.trim() === "Mein Team" || name.trim() === "Dein Team";

  const coachName =
    (userNameMe && userNameMe.trim().length > 0 ? userNameMe.trim() : null) ?? "Coach";
  // Header identity: top line shows the *club* (Verein) — crest/logo + name.
  // The team lives on the meta line underneath.
  const clubName = orgNameMe ?? "Dein Verein";
  const teamBaseName =
    activeTeamServer && !isAutoTeamName(activeTeamServer)
      ? activeTeamServer
      : (activeTeamServer ?? "Kein Team");
  const teamLeague = activeTeamLeagueMe?.trim() || null;
  const teamLine = teamLeague ? `${teamBaseName} (${teamLeague})` : teamBaseName;
  const coachImage = userImageMe ?? null;
  return (
    <div className="home-root">
      <div className="home-sweep" />
      <PitchBackdrop />
      <div className="home-grain" />

      <div className="home-stage home-scroll">
        <div className="area-header">
          <AppHeader
            mode="home"
            crest={clubName}
            crestUrl={orgLogo}
            teamName={clubName}
            meta={teamLine}
          />
        </div>

        <div className="area-identity">
          <div className="area-identity-panel">
            <CoachIdentity coachName={coachName} imageUrl={coachImage} />
          </div>
        </div>

        <div className="area-cards">
          <div className="home-cards">
            {cards.map((c, i) => (
              <ModuleCard
                key={c.title}
                title={c.title}
                meta={c.meta}
                Icon={c.Icon}
                accent={c.accent}
                delayMs={120 + i * 60}
                onClick={() => {
                  // Browser tile in web/PWA opens an external tab/window
                  // instead of the embedded /browser route — iframe sandboxing
                  // blocks too many sites (Google, banking, …) outside Tauri.
                  if (c.to === "/browser" && openExternalIfWeb("https://www.google.com/")) {
                    return;
                  }
                  navigate(c.to);
                }}
              />
            ))}
          </div>
        </div>

        <div className="area-dock">
          <StatusDock licenseValid={licenseValid} />
        </div>
      </div>
    </div>
  );
}
